import type { ListBuilderConfiguration } from '../types';

/**
 * Execution status value mapping
 */
const EXECUTION_STATUS_VALUE_MAPPING = [
	{
		value: 'batch_pending',
		label: 'Queued',
		color: { type: 'warning' },
	},
	{
		value: 'pending',
		label: 'Pending',
		color: { type: 'warning' },
	},
	{
		value: 'active',
		label: 'Active',
		color: { type: 'success' },
	},
	{
		value: 'completed',
		label: 'Completed',
		color: { type: 'success' },
	},
	{
		value: 'failed',
		label: 'Failed',
		color: { type: 'danger' },
	},
	{
		value: 'paused',
		label: 'Paused',
		color: { type: 'neutral' },
	},
	{
		value: 'batch_cancelled',
		label: 'Cancelled',
		color: { type: 'neutral' },
	},
	{
		value: 'excluded',
		label: 'Excluded',
		color: { type: 'neutral' },
	},
	// PD-14996 — a run an admin stopped (today only a search index rebuild).
	{
		value: 'cancelled',
		label: 'Cancelled',
		color: { type: 'neutral' },
	},
];

/**
 * Automation Reports list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const AUTOMATION_REPORTS_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-automation-reports',
			cacheTTL: 600000,
			name: 'All Executions',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'execution-id',
					headers: [{ id: 'execution-id-header', text: 'Execution' }],
					fields: [
						{
							id: 'id-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'id',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
							prefix: '#',
							link: {
								type: 'object',
								enabled: true,
							},
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: '_automation_execution',
							fieldName: 'id',
							direction: 'DESC',
						},
					],
					width: 100,
				},
				{
					id: 'flow-name',
					headers: [{ id: 'flow-name-header', text: 'Flow' }],
					fields: [
						{
							id: 'flow-name-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'version.flow.name',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
							link: {
								type: 'custom',
								enabled: true,
								urlTemplate:
									'/automation/flows/{version.flow.id}/version/{version.id}',
							},
						},
						{
							id: 'line-break-1',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							displayStyle: 'line_break',
						},
						{
							id: 'version-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'version.name',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
							emptyValue: '',
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: '_automation_version',
							fieldName: '_fk__automation_flow__id',
							direction: 'ASC',
						},
					],
					minWidth: 200,
				},
				{
					id: 'status',
					headers: [{ id: 'status-header', text: 'Status' }],
					fields: [
						{
							id: 'status-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'executionStatus',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'sm',
								valueMapping: EXECUTION_STATUS_VALUE_MAPPING,
							},
							displayStyle: 'badge',
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: '_automation_execution',
							fieldName: 'executionStatus',
							direction: 'ASC',
						},
					],
					width: 120,
				},
				{
					id: 'trigger',
					headers: [{ id: 'trigger-header', text: 'Trigger' }],
					fields: [
						{
							id: 'trigger-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'trigger.name',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
							emptyValue: 'Manual',
						},
					],
					width: 150,
				},
				{
					id: 'batch',
					headers: [{ id: 'batch-header', text: 'Batch' }],
					fields: [
						{
							id: 'batch-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'batch.name',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
							emptyValue: '-',
						},
					],
					width: 150,
				},
				{
					id: 'entity',
					headers: [{ id: 'entity-header', text: 'Entity' }],
					fields: [
						{
							id: 'entity-id-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'entityId',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
							emptyValue: '-',
						},
						{
							id: 'entity-line-break',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							displayStyle: 'line_break',
						},
						{
							id: 'entity-type-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'entityType',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
							emptyValue: '',
						},
					],
					width: 120,
				},
				{
					id: 'dedup-key',
					headers: [{ id: 'dedup-key-header', text: 'Dedup Key' }],
					fields: [
						{
							id: 'dedup-key-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'deduplicationKey',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
							emptyValue: '-',
						},
					],
					width: 150,
				},
				{
					id: 'started-at',
					headers: [{ id: 'started-at-header', text: 'Started' }],
					fields: [
						{
							id: 'started-at-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'startedAt',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
								formatOptions: { dateFormat: 'datetime-short' },
							},
							displayStyle: 'date',
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: '_automation_execution',
							fieldName: 'startedAt',
							direction: 'DESC',
						},
					],
					width: 150,
				},
				{
					id: 'duration',
					headers: [{ id: 'duration-header', text: 'Duration' }],
					fields: [
						{
							id: 'duration-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'duration',
									objectTable: 'automation_execution',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
							emptyValue: '-',
						},
					],
					width: 100,
				},
			],
			defaultSortId: 'started-at-desc',
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [10, 20, 50, 100],
	},
	filters: {
		activeFilters: [
			'automationExecutionReportKeywords',
			'automationExecutionReportStatus',
			'automationExecutionReportCreatedDate',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/automation/executions/:id',
			objectLabel: 'Execution',
			objectTable: '_automation_execution_report',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'executionStatus', fieldLabel: 'Status' },
				{ fieldName: 'entityType', fieldLabel: 'Entity Type' },
				{ fieldName: 'entityId', fieldLabel: 'Entity ID' },
				{ fieldName: 'deduplicationKey', fieldLabel: 'Dedup Key' },
				{ fieldName: 'startedAt', fieldLabel: 'Started At' },
				{ fieldName: 'completedAt', fieldLabel: 'Completed At' },
				{ fieldName: 'errorMessage', fieldLabel: 'Error Message' },
			],
		},
	],
};
